import { isNaN, uniqueId } from 'lodash';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  Automation,
  AutomationsApi,
  AutomationSource,
  AutomationTriggerType,
  AutomationType,
} from 'api/automations';
import { Field } from 'stores/fields';
import { refToAutomationSource } from 'utils/automations';
import {
  AutomationByReferenceId,
  useAddAutomation,
  useDeleteAutomation,
  useEditAutomation,
  useGetAutomationsForReferenceId,
} from './use-automations';

export type RefType = 'phase' | 'task';

interface Payload {
  field: Field;
  fromLabel?: string;
  toLabel: string;
  capturePlannedDate?: boolean;
  startOrEndDate?: 'startDate' | 'dueDate';
  updatePlannedDate?: boolean;
  refType: AutomationSource;
  refId: number;
  templateId: number;
}

const getCaptureActualEventPayload = (
  field: Field,
  refId: number,
  refType: Payload['refType'],
  startOrEndDate: Payload['startOrEndDate'],
  fromLabel: string | undefined,
  toLabel: string,
  triggerId = uniqueId(),
  actionId = uniqueId(),
) => ({
  automationName: `Custom Automation - ${Math.random()}`,
  automationType: AutomationType.KeyEvents,
  triggerV2: {
    uuid: triggerId,
    triggerType: AutomationTriggerType.EventTrigger,
    template:
      refType === AutomationSource.ProjectPhaseTemplate
        ? 'ANY_PHASE_STATUS_IS_CHANGED'
        : 'ANY_TASK_STATUS_IS_UPDATED',
    input: {
      fromFieldLabel: fromLabel,
      toFieldLabel: toLabel,
    },
  },
  actionsV2: [
    {
      uuid: actionId,
      template: 'STORE_KEY_EVENT_VALUE',
      input: {
        fieldId: field.fieldId,
      },
    },
  ],
  enabled: true,
  tags: {
    tag: `key-event:${field.fieldId}__ref-id:${refId}__capture-actual`,
    referenceId: refId,
    referenceType: refType,
    keyEventAutomationType: 'capture-actual',
    fieldId: field.fieldId,
  },
});

const getCapturePlannedEventPayload = (
  field: Field,
  refId: number,
  refType: Payload['refType'],
  startOrEndDate: Payload['startOrEndDate'],
  triggerId = uniqueId(),
  actionId = uniqueId(),
) => ({
  automationName: `Custom Automation - ${Math.random()}`,
  automationType: AutomationType.KeyEvents,
  tags: {
    tag: `key-event:${field.fieldId}__ref-id:${refId}__capture-expected`,
    referenceId: refId,
    referenceType: refType,
    keyEventAutomationType: 'capture-expected',
    fieldId: field.fieldId,
  },
  triggerV2: {
    triggerType: AutomationTriggerType.EventTrigger,
    uuid: triggerId,
    template:
      refType === AutomationSource.ProjectPhaseTemplate
        ? 'PHASE_CREATED'
        : 'TASK_CREATED',
    input: {
      fieldName: startOrEndDate,
      fieldId: (field.metaData as any)?.relatedFieldId,
    },
  },
  actionsV2: [
    {
      uuid: actionId,
      template: 'STORE_KEY_EVENT_VALUE',
      input: {
        fieldId: (field.metaData as any)?.relatedFieldId,
      },
    },
  ],
  enabled: true,
});

const getUpdatePlannedEventPayload = (
  field: Field,
  refId: number,
  refType: Payload['refType'],
  startOrEndDate: Payload['startOrEndDate'],
  triggerId = uniqueId(),
  actionId = uniqueId(),
) => ({
  automationName: `Custom Automation - ${Math.random()}`,
  automationType: AutomationType.KeyEvents,
  triggerV2: {
    triggerType: AutomationTriggerType.EventTrigger,
    uuid: triggerId,
    template:
      refType === AutomationSource.ProjectPhaseTemplate
        ? startOrEndDate === 'dueDate'
          ? 'ANY_PHASE_DUE_DATE_IS_CHANGED'
          : 'ANY_PHASE_START_DATE_IS_CHANGED'
        : 'ANY_TASK_FIELD_IS_UPDATED',
    input:
      refType === AutomationSource.TaskTemplate
        ? { fieldName: startOrEndDate }
        : {},
  },
  tags: {
    fieldId: field.fieldId,
    tag: `key-event:${field?.fieldId}__ref-id:${refId}__update-expected`,
    referenceId: refId,
    referenceType: refType,
    keyEventAutomationType: 'update-expected',
  },
  actionsV2: [
    {
      uuid: actionId,
      template: 'STORE_KEY_EVENT_VALUE',
      input: {
        fieldId: (field?.metaData as any)?.relatedFieldId,
      },
    },
  ],
  enabled: true,
});

export const useAddKeyEventAutomation = (): UseMutationResult<
  void,
  unknown,
  Payload,
  unknown
> => {
  const qc = useQueryClient();

  return useMutation(
    async ({
      refId,
      refType,
      fromLabel,
      toLabel,
      field,
      capturePlannedDate,
      startOrEndDate,
      updatePlannedDate,
      templateId,
    }: Payload) => {
      const mainAutomationPayload: Partial<Automation> =
        getCaptureActualEventPayload(
          field,
          refId,
          refType,
          startOrEndDate,
          fromLabel,
          toLabel,
        );

      await AutomationsApi.createAutomationForReferenceId(
        templateId,
        refType,
        refId,
        mainAutomationPayload,
      );

      if (capturePlannedDate) {
        const capturePlannedDateAutomationPayload =
          getCapturePlannedEventPayload(field, refId, refType, startOrEndDate);

        await AutomationsApi.createAutomationForReferenceId(
          templateId,
          refType,
          refId,
          capturePlannedDateAutomationPayload,
        );

        if (updatePlannedDate) {
          const updatePlannedDatePayload = getUpdatePlannedEventPayload(
            field,
            refId,
            refType,
            startOrEndDate,
          );

          await AutomationsApi.createAutomationForReferenceId(
            templateId,
            refType,
            refId,
            updatePlannedDatePayload,
          );
        }
      }
    },

    {
      onSuccess: async (_, variables) => {
        const { refType, refId } = variables;

        await qc.refetchQueries(AutomationByReferenceId(refType, refId));
      },
    },
  );
};

type UpdateKeyEventPayload = Partial<
  Omit<Payload, 'refType' | 'refId' | 'toLabel' | 'templateId'>
> &
  Pick<Payload, 'refType' | 'refId' | 'toLabel' | 'templateId'>;

export const useUpdateKeyEventAutomation = (): UseMutationResult<
  void,
  unknown,
  UpdateKeyEventPayload,
  unknown
> => {
  const qc = useQueryClient();
  const { mutateAsync: deleteAutomation } = useDeleteAutomation();
  const { mutateAsync: addAutomation } = useAddAutomation();
  const { mutateAsync: editAutomation } = useEditAutomation();

  return useMutation(
    async ({
      refId,
      refType,
      field,
      capturePlannedDate,
      startOrEndDate,
      updatePlannedDate,
      fromLabel,
      toLabel,
      templateId,
    }: UpdateKeyEventPayload) => {
      const data: Array<Automation> | undefined = qc.getQueryData(
        AutomationByReferenceId(refType, refId),
      );

      const mainAutomation = data?.find(
        (e) =>
          e.triggerV2.template === 'ANY_PHASE_STATUS_IS_CHANGED' ||
          e.triggerV2.template === 'ANY_TASK_STATUS_IS_CHANGED',
      );

      const isCaptureExpectedDateAvailable = data?.find(
        (e) =>
          e.tags.fieldId === field?.fieldId &&
          e.tags.keyEventAutomationType === 'capture-expected' &&
          e.tags.referenceId === refId &&
          e.tags.referenceType === refType,
      );

      const isUpdateExpectedDateAvailable = data?.find(
        (e) =>
          e.tags.fieldId === field?.fieldId &&
          e.tags.keyEventAutomationType === 'update-expected' &&
          e.tags.referenceId === refId &&
          e.tags.referenceType === refType,
      );

      if (!field) {
        return;
      }

      const mainAutomationPayload = getCaptureActualEventPayload(
        field,
        refId,
        refType,
        startOrEndDate,
        fromLabel,
        toLabel,
      );

      if (mainAutomation) {
        await editAutomation({
          automationId: mainAutomation.automationId,
          automation: mainAutomationPayload,
        });
      }

      if (Boolean(isCaptureExpectedDateAvailable) !== capturePlannedDate) {
        if (capturePlannedDate && !isCaptureExpectedDateAvailable) {
          await addAutomation({
            templateId,
            automation: getCapturePlannedEventPayload(
              field,
              refId,
              refType,
              'startDate',
            ),
            referenceType: refType,
            referenceId: refId,
          });
          if (updatePlannedDate && !isUpdateExpectedDateAvailable) {
            await addAutomation({
              templateId,
              automation: getUpdatePlannedEventPayload(
                field,
                refId,
                refType,
                'startDate',
              ),
              referenceType: refType,
              referenceId: refId,
            });
          } else if (!updatePlannedDate && !!isUpdateExpectedDateAvailable) {
            await deleteAutomation({
              automationId: isUpdateExpectedDateAvailable.automationId,
            });
          }
        } else if (isCaptureExpectedDateAvailable && !capturePlannedDate) {
          await deleteAutomation({
            automationId: isCaptureExpectedDateAvailable.automationId,
          });
          if (isUpdateExpectedDateAvailable) {
            await deleteAutomation({
              automationId: isUpdateExpectedDateAvailable.automationId,
            });
          }
        } else if (capturePlannedDate && isCaptureExpectedDateAvailable) {
          await editAutomation({
            automationId: isCaptureExpectedDateAvailable.automationId,
            automation: getCapturePlannedEventPayload(
              field,
              refId,
              refType,
              startOrEndDate,
            ),
          });
          if (isUpdateExpectedDateAvailable && !updatePlannedDate) {
            await deleteAutomation({
              automationId: isUpdateExpectedDateAvailable.automationId,
            });
          } else if (updatePlannedDate && !isUpdateExpectedDateAvailable) {
            await addAutomation({
              automation: getUpdatePlannedEventPayload(
                field,
                refId,
                refType,
                'startDate',
              ),
              referenceType: refType,
              referenceId: refId,
              templateId,
            });
          }
        }
      } else {
        if (isCaptureExpectedDateAvailable) {
          await editAutomation({
            automationId: isCaptureExpectedDateAvailable.automationId,
            automation: getCapturePlannedEventPayload(
              field,
              refId,
              refType,
              startOrEndDate,
            ),
          });
        }
        if (updatePlannedDate && !isUpdateExpectedDateAvailable) {
          await addAutomation({
            automation: getUpdatePlannedEventPayload(
              field,
              refId,
              refType,
              'startDate',
            ),
            referenceType: refType,
            referenceId: refId,
            templateId,
          });
        } else if (!updatePlannedDate && isUpdateExpectedDateAvailable) {
          await deleteAutomation({
            automationId: isUpdateExpectedDateAvailable.automationId,
          });
        }
      }
    },
    {
      onSuccess: async (_, variables) => {
        const { refType, refId } = variables;

        await qc.refetchQueries(AutomationByReferenceId(refType, refId));
      },
    },
  );
};

export const useGetKeyEventMutationsForRefId = (
  templateId: number,
  refType: string,
  refId: number,
): { isLoading: boolean; data: Array<Automation> } => {
  const { data, isLoading } = useGetAutomationsForReferenceId({
    templateId,
    refId,
    refType: refToAutomationSource(refType),
  });

  return {
    isLoading,
    data:
      data?.filter(
        (e) =>
          e.triggerV2.template === 'ANY_TASK_STATUS_IS_UPDATED' ||
          e.triggerV2.template === 'ANY_PHASE_STATUS_IS_CHANGED',
      ) ?? [],
  };
};
export const useGetCaptureExpectedEventAutomationForRefId = (
  templateId: number,
  refType: string,
  refId: number,
  fieldId: number | undefined,
) => {
  const { data } = useGetAutomationsForReferenceId({
    templateId,
    refType: refToAutomationSource(refType),
    refId,
  });

  if (!fieldId || isNaN(refId)) {
    return undefined;
  }

  return data?.find(
    (e) =>
      e.tags.fieldId === fieldId &&
      e.tags.keyEventAutomationType === 'capture-expected' &&
      e.tags.referenceId === refId &&
      e.tags.referenceType === refToAutomationSource(refType),
  );
};

export const useUpdateExpectedEventAutomationForRefId = (
  templateId: number,
  refType: string,
  refId: number,
  fieldId: number | undefined,
): Automation | undefined => {
  const { data } = useGetAutomationsForReferenceId({
    templateId,
    refType: refToAutomationSource(refType),
    refId,
  });

  if (!fieldId || isNaN(refId)) {
    return undefined;
  }

  return data?.find(
    (e) =>
      e.tags.fieldId === fieldId &&
      e.tags.keyEventAutomationType === 'update-expected' &&
      e.tags.referenceId === refId &&
      e.tags.referenceType === refToAutomationSource(refType),
  );
};

export const useAlreadyTakenKeyEventsForThisRef = (
  templateId: number,
  refType: Payload['refType'],
  refId: number,
): Automation[] => {
  const { data } = useGetAutomationsForReferenceId({
    refType: refToAutomationSource(refType),
    templateId,
    refId,
  });

  return (
    data?.map((e) => e.actionsV2[0]?.input?.fieldId).filter((e) => !!e) ?? []
  );
};
